import {
  BehaviorSubject,
  combineLatest,
  from,
  map,
  merge,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { IMessage } from '../data/message';
import {
  collectMessages,
  filterByKey,
  findAndOverwrite,
  mapToMessageEvent,
} from '../rxjs';

// TODO(architecture): Rework so that opening a message returns
// something like a `MessageRef` and only once the message should
// be shown instanciate a component for it. @angular/material does
// provide utilities for this via Overlay / Dialog.
/**
 * Abstract implementation of messaging service.
 * Provides an interface to communicate with the nearest message stack.
 */
export abstract class MessageService<T extends IMessage> {
  private _messagesSubject = new BehaviorSubject<T[]>([]);
  private _messagesOpenSubject = new Subject<Partial<T>>();
  private _messagesClosedSubject = new Subject<T['key']>();

  private openMessage$ = combineLatest([
    this._messagesSubject.asObservable(),
    this._messagesOpenSubject.asObservable(),
  ]).pipe(findAndOverwrite<T>({ shown: true }));

  private closeMessage$ = combineLatest([
    this._messagesSubject.asObservable(),
    this._messagesClosedSubject.pipe(map((key) => ({ key }) as Partial<T>)),
  ]).pipe(findAndOverwrite({ shown: false }));

  /**
   * Stream of message events
   */
  public messageEvents$ = merge(
    this.openMessage$.pipe(mapToMessageEvent('open')),
    this.closeMessage$.pipe(mapToMessageEvent('close')),
  );

  /**
   * Stream of open messages
   */
  public readonly messages$ = this.messageEvents$.pipe(
    collectMessages(),
    shareReplay(1),
  );

  /**
   * Adds the given message to the collection of messages
   *
   * @param message The message to add
   */
  public add(message: T) {
    this._messagesSubject.next([...this._messagesSubject.value, message]);
  }

  /**
   * Removes the given message from collection of messages
   *
   * @param message The message to remove
   */
  public remove(message: T) {
    this.removeByKey(message.key);
  }

  /**
   * Removes a message from the collection of messages by using the `key`.
   *
   * @param key The key of the message to remove
   */
  public removeByKey(key: T['key']) {
    this._messagesSubject.next(
      this._messagesSubject.value.filter((t) => t.key !== key),
    );
  }

  /**
   * Opens the message with the given `key`.
   *
   * @param key The key of the message to open
   * @param actions Callbacks
   */
  public open(key: T['key'], actions: T['actions']) {
    this.openWithOverrides(key, { actions } as Partial<T>);
  }

  /**
   * Opens the message with the given `key`,
   * but takes overrides for the message
   *
   * @param key Key of the message to open
   * @param msg Overrides for the message to be open
   */
  protected openWithOverrides(key: T['key'], msg: Partial<T>) {
    this._messagesOpenSubject.next({
      ...msg,
      key,
    });
  }

  /**
   * Closes the message with the given `key`.
   *
   * @param key The key of the message to close
   */
  public close(key: T['key']) {
    this._messagesClosedSubject.next(key);
  }

  /**
   * @returns
   * An `Observable` that emits the with the version
   * of the modal specified by `key`.
   */
  public trackByKey(key: T['key']): Observable<T> {
    const originalMsg$ = this._messagesSubject.pipe(
      switchMap((messages) => from(messages)),
      filterByKey(key),
    );

    // Emits with the open message or with undefined if the message
    // isn't currently shown
    const openMsg$ = this.messages$.pipe(
      startWith([]),
      map((messages) => messages.find((m) => m.key === key)),
    );

    // Emit the currently open message if it is open
    // or with the original message.
    // This is used to show a message as open even if the
    // actual message instance was removed and re-added.
    return combineLatest([originalMsg$, openMsg$]).pipe(
      map(([originalMsg, openMsg]) => openMsg ?? originalMsg),
    );
  }
}
